import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { EMPTY_MESSAGES } from '@/shared/lib/constants';
import { experimentKeys } from '@/shared/lib/query/queryKeys';
import { sortTagsSafe } from '@/shared/lib/sort';
import type { BadgeItem } from '@/shared/types/badge';
import { BadgeList } from '@/shared/ui/BadgeList';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';

import type { AssetDetailResponse } from '@/entities/asset-search/domain/types';
import { updateRunTags } from '@/entities/experiment/api/experimentTagsApi';

import { ExperimentTagsEditModal } from '@/widgets/experiment-asset-detail/ExperimentTagsEditModal';

import styles from './ExperimentAssetDetailSidebar.module.css';

type ExperimentAssetDetailSidebarProps = {
  experimentTags?: AssetDetailResponse['experimentTags'];
  assetId: string;
  assetType: string;
  runIdNumber?: number;
  snapshotId?: string;
};

export const ExperimentAssetDetailSidebar: React.FC<ExperimentAssetDetailSidebarProps> = ({
  experimentTags,
  assetId,
  assetType,
  runIdNumber,
  snapshotId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  const runId = useMemo(() => {
    if (assetType === 'run') {
      const parsed = parseInt(assetId, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }, [assetId, assetType]);

  const tagBadgeItems = useMemo<BadgeItem[]>(() => {
    const sortedTags = sortTagsSafe(experimentTags);
    return sortedTags.map((tag) => ({
      tagFQN: tag,
      name: tag,
    }));
  }, [experimentTags]);

  const updateTagsMutation = useMutation({
    mutationFn: async (newTags: string[]) => {
      if (!runId) {
        throw new Error('Run ID가 없습니다.');
      }
      await updateRunTags(runId, experimentTags || [], newTags);
    },
    onSuccess: () => {
      message.success('태그가 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
      const baseQueryKey = experimentKeys.assetDetail(assetId, assetType, runIdNumber);
      if (snapshotId) {
        queryClient.invalidateQueries({ queryKey: [...baseQueryKey, snapshotId] });
      } else {
        queryClient.invalidateQueries({ queryKey: baseQueryKey });
      }
    },
    onError: (error) => {
      console.error('태그 업데이트 실패:', error);
    },
  });

  const handleSubmit = useCallback(
    (newTags: string[]) => {
      updateTagsMutation.mutate(newTags);
    },
    [updateTagsMutation]
  );

  return (
    <div className={styles.sidebar}>
      <div className={styles.tagSection}>
        <div className={styles.titleCont}>
          <p className={styles.title}>{assetType === 'run' ? '태그' : '런 태그'}</p>
          {assetType === 'run' && (
            <IconButton size="small" onClick={() => setIsEditing(true)} tooltip="태그 편집">
              <Icon name="edit" size={16} />
            </IconButton>
          )}
        </div>
        <BadgeList items={tagBadgeItems} showIcon isEllipsis showTooltip emptyText={EMPTY_MESSAGES.NO_TAGS} />
      </div>

      <ExperimentTagsEditModal
        open={isEditing}
        tags={experimentTags || []}
        onClose={() => setIsEditing(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
